import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { IChangePasswordRM, ILoginRM, IUserDTO } from '../data-access/models/request/user-auth';
import { AuthenticateTempUserAPI, AuthenticateUserAPI, ChangePasswordAPI, GetCurrentUserAPI, UpdateUserProfileAPI } from '../data-access/api-services';
import { AuthStore } from 'src/utils/auth-store';
import { AppRoute } from 'src/router/route-config';
import { IAuthDTO, ITempAuthDTO } from '../data-access/models/response/auth';
import { AlertDialog } from '../components/common/dialog-boxes/alert-dialog';
import { CommonHelper } from 'src/utils/common-helper';
import moment from 'moment';
import Toaster from '../utils/toaster';
import { SolutionUserRole, SolutionUserStatus, UserRole, VEAnalysisLiskShareType } from 'src/data-access/models/enums';
import { CIAnalysis_StepType } from 'src/components/analysis/manage/ci/layout';
import { VEAnalysis_StepType } from 'src/components/analysis/manage/ve/layout';
import { IAPIDataResult } from 'src/data-access/models/response/api-result';

export interface ICIAnalysisChatbotContext {
    step: CIAnalysis_StepType;
    accountName: string;
    website?: string | null;
    solutionNames?: string;
    clientId?: string;
    clientName?: string;
    clientWebsite?: string;
    industry: string;
}

export interface IVEAnalysisChatbotContext {
    step: VEAnalysis_StepType;
    accountName: string;
    solutionName: string;
    clientId?: string;
    clientName?: string;
    clientWebsite?: string;
}

export interface IChatbotContext {
    ci?: ICIAnalysisChatbotContext;
    ve?: IVEAnalysisChatbotContext;
}

interface AppContextType {
    currentUser: IUserDTO | undefined;
    guestUser: ITempAuthDTO | undefined;
    isLoadingCurrentUser: boolean;
    checkIsTokenValid: () => boolean;
    getRoleFromStore: () => UserRole | undefined;
    getAuth: () => IAuthDTO | ITempAuthDTO | undefined;
    getAccessToken: () => string | undefined;
    loginAsync: (request: ILoginRM) => Promise<IAuthDTO | undefined>;
    guestLoginAsync: (token: string) => Promise<ITempAuthDTO | string>;
    changePasswordAsync: (request: IChangePasswordRM) => Promise<boolean>;
    updateUserProfileAsync: (request: any) => Promise<IAPIDataResult<IUserDTO>>;
    logout: (isRedirectToLogin: boolean) => void;
    getSessionExpireInMS: () => number | undefined;
    fetchCurrentUser: () => void;
    chatbotContext: IChatbotContext | undefined;
    updateChatbotContext: (context: IChatbotContext) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<IUserDTO | undefined>(undefined);
    const [guestUser, setGuestUser] = useState<ITempAuthDTO | undefined>(undefined);
    const [isAuthChecked, setIsAuthChecked] = useState(false);
    const [isLoadingCurrentUser, setIsLoadingCurrentUser] = useState<boolean>(true);
    const sessionTimer = useRef<number | null>(null);
    const [chatbotContext, setChatbotContext] = useState<IChatbotContext | undefined>(undefined);

    const loginAsync = (request: ILoginRM): Promise<IAuthDTO | undefined> => {
        return new Promise((resolve) => {
            AuthenticateUserAPI(request)
                .then(authResult => {
                    if (authResult.isSuccess) {
                        AuthStore.setAuth(authResult.data);
                        GetCurrentUserAPI()
                            .then(userResponse => {
                                beforeSetCurrentUser(userResponse.data);
                                resolve(authResult.data);

                                if (sessionTimer.current) clearTimeout(sessionTimer.current);
                                sessionTimer.current = window.setTimeout(showSessionTimeoutDialog, CommonHelper.getUtcDateTimeStrToMomentLocal(authResult.data.accessTokenValidTo).diff(moment()));
                            })
                            .catch(() => resolve(undefined))
                    }
                    else {
                        if (authResult.data) {
                            let data = authResult.data as any;
                            switch (Number(data.status)) {
                                case 1:
                                    Toaster.error(authResult.message);
                                    break;
                                case 3:
                                    AlertDialog.show({
                                        content: authResult.message,
                                        title: "Access Rights Revoked",
                                        type: "danger"
                                    });
                                    break;
                                case 2:
                                case 4:
                                    AlertDialog.show({
                                        content: authResult.message,
                                        title: "Authentication Error",
                                        type: "danger"
                                    });
                                    break;
                            }
                        }
                        else if (authResult.message)
                            Toaster.error(authResult.message);
                        resolve(undefined);
                    }
                })
                .catch(() => {
                    resolve(undefined);
                });
        });
    };

    const guestLoginAsync = (token: string): Promise<ITempAuthDTO | string> => {
        return new Promise((resolve) => {
            AuthenticateTempUserAPI(token)
                .then(authResult => {
                    if (authResult.isSuccess) {
                        AuthStore.setAuth(authResult.data);
                        resolve(authResult.data);

                        if (sessionTimer.current) clearTimeout(sessionTimer.current);
                        sessionTimer.current = window.setTimeout(showSessionTimeoutDialog, CommonHelper.getUtcDateTimeStrToMomentLocal(authResult.data.accessTokenValidTo).diff(moment()));
                    }
                    else
                        resolve(authResult.message);
                })
                .catch(() => resolve("Something went wrong. Please reload page."));
        });
    };

    const updateUserProfileAsync = (requestdata: any): Promise<IAPIDataResult<IUserDTO>> => {
        return new Promise((resolve, reject) => {
            UpdateUserProfileAPI(requestdata)
                .then((result: IAPIDataResult<IUserDTO>) => {
                    if (result.isSuccess) {
                        setCurrentUser(result.data);

                        //Update auth data to reflect the changes on header
                        let auth = AuthStore.getAuth() as IAuthDTO | undefined;
                        if (auth) {
                            auth.fullName = result.data.firstName + " " + result.data.lastName;
                            auth.profilePicture = result.data.profilePhoto;
                            AuthStore.setAuth(auth);
                        }

                        resolve(result);
                    }
                })
                .catch(error => {
                    reject(error);
                });
        });
    };

    const changePasswordAsync = (request: IChangePasswordRM): Promise<boolean> => {
        return new Promise((resolve) => {
            ChangePasswordAPI(request)
                .then(authResult => {
                    if (authResult.isSuccess) {
                        AuthStore.setAuth(authResult.data);
                        GetCurrentUserAPI()
                            .then(userResponse => {
                                if (userResponse.isSuccess) {
                                    beforeSetCurrentUser(userResponse.data);
                                    resolve(true);
                                } else {
                                    resolve(false);
                                }
                            })
                            .catch(() => {
                                resolve(false);
                            })
                    }
                    else {
                        resolve(false);
                    }
                })
                .catch(() => {
                    resolve(false);
                });
        });
    }

    const checkIsSolutionAdmin = (userDTO: IUserDTO): boolean => {
        if (userDTO.role !== UserRole.SolutionUser) return false;

        const isSolutionAdmin = userDTO.clientAccesses.length > 0 &&
            (
                userDTO.clientAccesses[0].isSolutionAdminForNewSolution ||
                userDTO.clientAccesses[0].solutionAccesses
                    .some(solutionRole =>
                        solutionRole.status === SolutionUserStatus.Approved &&
                        solutionRole.role === SolutionUserRole.SolutionAdmin &&
                        !solutionRole.solution.isDeactivated
                    )
            ) ? true : false;

        return isSolutionAdmin;
    }

    const logout = (isRedirectToLogin: boolean) => {
        AuthStore.removeAuth();
        if (sessionTimer.current) clearTimeout(sessionTimer.current);
        if (isRedirectToLogin) {
            const cacheBustedUrl = `${guestUser ? AppRoute.GuestLogoutSuccess : AppRoute.SignIn}?${guestUser ? guestUser.shareType === VEAnalysisLiskShareType.KeyInputsOnly ? "q=ki&" : "a=fa&" : ""}d=${CommonHelper.encrypt(new Date().getTime().toString())}`;
            window.location.href = cacheBustedUrl;
        }
    }

    const checkIsTokenValid = () => AuthStore.checkUserAccessTokenValid();

    const getRoleFromStore = () => AuthStore.getRole();

    const getAuth = () => AuthStore.getAuth();

    const getAccessToken = () => AuthStore.getAccessToken();

    const getSessionExpireInMS = (): number | undefined => {
        const storedAccessTokenDt = getAuth()?.accessTokenValidTo;
        if (storedAccessTokenDt) {
            return CommonHelper.getUtcDateTimeStrToMomentLocal(storedAccessTokenDt).diff(moment());
        }
    };

    const showSessionTimeoutDialog = () => {
        AlertDialog.show({
            title: "Session Expired!",
            content: "Your session has timed out. You need to login again.",
            type: 'danger',
            onClose: () => { logout(true); }
        });
    };

    const fetchCurrentUser = () => {
        setIsLoadingCurrentUser(true);
        GetCurrentUserAPI()
            .then(result => {
                if (result.isSuccess) {
                    beforeSetCurrentUser(result.data);
                    setIsAuthChecked(true);
                }
            })
            .finally(() => setIsLoadingCurrentUser(false));
    }

    const beforeSetCurrentUser = (apiUser: IUserDTO) => {
        const updatedData: IUserDTO = {
            ...apiUser,
            isSolutionAdmin: checkIsSolutionAdmin(apiUser),
            hasAccessToAnySolution: (apiUser.clientAccesses.flatMap(x => x.solutionAccesses).length ?? 0) > 0
        };

        setCurrentUser(updatedData);
    }

    const updateChatbotContext = (context: IChatbotContext) => {
        setChatbotContext(context);
    }

    useEffect(() => {
        const isGuest = (AuthStore.getAuth() as ITempAuthDTO | undefined)?.shareId !== undefined;
        if (isGuest && AuthStore.checkUserAccessTokenValid()) {
            setGuestUser(AuthStore.getAuth() as ITempAuthDTO);
            setIsAuthChecked(true);
            return;
        }

        if (!getAccessToken()) {
            setIsAuthChecked(true);
            return;
        }

        if (!checkIsTokenValid()) {
            setTimeout(() => { showSessionTimeoutDialog(); }, 500);
            setIsAuthChecked(true);
            return;
        }

        const storedAccessTokenDt = getAuth()?.accessTokenValidTo;
        if (storedAccessTokenDt) {
            let diff = CommonHelper.getUtcDateTimeStrToMomentLocal(storedAccessTokenDt).diff(moment());

            if (diff < 500) {
                setTimeout(() => { showSessionTimeoutDialog(); }, 500);
                setIsAuthChecked(true);
                return;
            }
            else {
                if (sessionTimer.current) clearTimeout(sessionTimer.current);
                sessionTimer.current = window.setTimeout(showSessionTimeoutDialog, diff);
            }
        }

        fetchCurrentUser();
    }, []);

    return (
        <AppContext.Provider value={{
            currentUser,
            guestUser,
            isLoadingCurrentUser,
            logout,
            loginAsync,
            guestLoginAsync,
            updateUserProfileAsync,
            getRoleFromStore,
            checkIsTokenValid,
            getAuth,
            getAccessToken,
            changePasswordAsync,
            getSessionExpireInMS,
            fetchCurrentUser,
            chatbotContext,
            updateChatbotContext
        }}>
            {isAuthChecked ?
                <>{children}</> :
                <div className='page-loader-container show fade'>
                    <div>
                        <img src="/images/logo-sm.svg" alt='logo' />
                        <div className="spinner-loader"></div>
                    </div>
                </div>
            }
        </AppContext.Provider>
    );
};

export const useApplicationContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAuthContext must be used within a AuthProvider');
    }
    return context;
};